import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from 'react-router-dom';
import { formatCpf, formatCnpj } from '@brazilian-utils/brazilian-utils';
import api from '../../services/api';
import {
  DEFAULT_ESFERA_PRIVADA,
  PUBLICA_VALUE,
  getEsferaDisplayValue,
  normalizeNaturezaFields,
} from '../../utils/naturezaJuridica';

const FormContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 30px auto 50px auto; 
  padding: 30px 24px;
  background-color: #FFFFFF;
  border-radius: 11px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  max-height: 90vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto; /* centraliza horizontalmente */
  margin-bottom: 30px;
  width: 80%;
`;

const Title = styled.h3`
  text-align: center;
  font-weight: 800;   /* mais forte que bold padrão */
  font-size: 1.2rem;
  color: #f28c28;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px 15px;
  margin: 0 auto;
  width: 80%;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  
  &.span-2 {
    grid-column: span 2;
  }
  
  &.span-3 {
    grid-column: span 3;
  }
`;

const Label = styled.label`
  font-size: 0.9rem;
  box-sizing: border-box;
  font-weight: 700;
  color: #333;
`;

const Value = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const FileSection = styled.div`
  margin: 0 auto; /* centraliza horizontalmente */
  margin-top: 10px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
  max-width: 80%;
`;

const FileLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #6AB8A8;
  margin-bottom: 10px;
`;

const FileContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: white;
  border: 1px dashed #ccc;
  border-radius: 5px;
`;

const FileName = styled.span`
  color: #6AB8A8;
  font-size: 0.9rem;
`;

const SubstituirButton = styled.button`
  padding: 5px 15px;
  background-color: transparent;
  color: #f7921e;
  border: 1px solid #f7921e;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.85rem;
  
  &:hover {
    background-color: #fff5e6;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 15px;
`;

const Button = styled.button`
  background-color: #6ab292;
  color: white;
  font-size: 16px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  padding: 8px 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #5a9e82;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #c0392b;
  background-color: #fdf2f2;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 12px;
  margin: 15px auto;
  width: 80%;
  text-align: center;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.p`
  color: #28a745;
  font-size: 13px;
  font-family: "Public Sans";
  text-align: center;
  margin-top: 15px;
  padding: 8px;

  @media (max-width: 480px) {
    font-size: 12px;
    margin-top: 10px;
    padding: 6px;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const FormComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [termoInfo, setTermoInfo] = useState(null);
  const [termoData, setTermoData] = useState(null);
  const [finalizado, setFinalizado] = useState(false);

  const formData = useMemo(() => {
    if (location.state) return normalizeNaturezaFields(location.state);
    const saved = localStorage.getItem('cadastroInstituicaoForm');
    try {
      const parsed = saved ? JSON.parse(saved) : {};
      return normalizeNaturezaFields(parsed);
    } catch (_) {
      return normalizeNaturezaFields({});
    }
  }, [location.state]);


  useEffect(() => {
    const loadTermoInfo = async () => {
      try {
        const termo = await api.getMeuTermo();
        setTermoInfo(termo);
        
        const savedTermoData = localStorage.getItem('termoUploadResponse');
        if (savedTermoData) {
          const parsedData = JSON.parse(savedTermoData);
          setTermoData(parsedData);
        }
      } catch (error) {
        console.error('Erro ao carregar termo:', error);
        setError('Erro ao carregar informações do termo. Faça o upload do termo primeiro.');
      }
    };
    loadTermoInfo();
  }, []);

  const handleBefore = () => {
    navigate('/CadastroInstituicao');
  };

  const handleNext = async () => {
    if (!termoInfo) {
      setError('Informações do termo não encontradas. Faça o upload do termo primeiro.');
      return;
    }

    if (!formData.cnpj) {
      setError('CNPJ é obrigatório.');
      return;
    }

    setLoading(true);
    setError('');
    setSucesso('');

    try {   
      const payload = {
        cnpj: formData.cnpj,
        numeroCooperacao: termoData.numeroCooperacao || formData.numeroCooperacao || '',
        vigenciaTermo: termoData.vigenciaTermo || formData.vigenciaTermo || '',
        dadosInstituicao: {
          nomeRegistroMEC: formData.nomeRegistroMEC,
          sigla: formData.sigla,
          razaoSocial: formData.razaoSocial,
          naturezaJuridica: formData.naturezaJuridica,
          esfera: formData.esfera,
          endereco: formData.endereco,
          numero: formData.numero,
          bairro: formData.bairro,
          cidadeId: formData.cidadeId,
          cidade: formData.cidade,
          estado: formData.estado,
          cep: formData.cep,
          telefone: formData.telefone,
          telefone2: formData.telefone2,
          email: formData.email,
          email2: formData.email2,
          reitorDiretor: formData.reitorDiretor,
          cpfReitorDiretor: formData.cpfReitorDiretor,
          emailReitorDiretor: formData.emailReitorDiretor,
          telefoneReitorDiretor: formData.telefoneReitorDiretor,
        }
      };

      const result = await api.criarInstituicaoCadastro(payload);

      await api.enviarCadastroParaAnalise();

      localStorage.clear();
      
      setFinalizado(true);
      setSucesso('Cadastro da instituição enviado para análise com sucesso! Redirecionando...');
      setTimeout(() => {
        navigate('/Login', { 
          state: { 
            message: 'Cadastro da instituição enviado para análise com sucesso!',
            cadastroId: result.id 
          } 
        });
      }, 2500);
      
    } catch (error) {
      console.error('Erro ao criar cadastro:', error);
      setError(error.message || 'Erro ao criar cadastro da instituição.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer>
      <Header>
        <Title>CONFIRA OS DADOS DA INSTITUIÇÃO</Title>
      </Header>

      <ContentGrid>
        <FieldGroup className="span-2">
          <Label>Nome de registro no MEC*</Label>
          <Value>{formData.nomeRegistroMEC}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Natureza jurídica*</Label>
          <Value>{formData.naturezaJuridica || '-'}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Esfera*</Label>
          <Value>{getEsferaDisplayValue(formData.naturezaJuridica, formData.esfera) || DEFAULT_ESFERA_PRIVADA}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Sigla</Label>
          <Value>{formData.sigla}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>CNPJ*</Label>
          <Value>{formData.cnpj ? formatCnpj(formData.cnpj) : '-'}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Razão social*</Label>
          <Value>{formData.razaoSocial}</Value>
        </FieldGroup>

        <FieldGroup className="span-2">
          <Label>Endereço*</Label>
          <Value>{formData.endereco}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Número*</Label>
          <Value>{formData.numero}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Bairro*</Label>
          <Value>{formData.bairro}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Cidade*</Label>
          <Value>{formData.cidadeId}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Estado*</Label>
          <Value>{formData.estado}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>CEP*</Label>
          <Value>{formData.cep}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Telefone *</Label>
          <Value>{formData.telefone}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Telefone (opcional)</Label>
          <Value>{formData.telefone2}</Value>
        </FieldGroup>

        <FieldGroup className="span-2">
          <Label>E-mail*</Label>
          <Value>{formData.email}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>E-mail (opcional)</Label>
          <Value>{formData.email2 || ''}</Value>
        </FieldGroup>

        <FieldGroup className="span-2">
          <Label>Reitor/Diretor*</Label>
          <Value>{formData.reitorDiretor}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>CPF*</Label>
          <Value>{formData.cpfReitorDiretor ? formatCpf(formData.cpfReitorDiretor) : '-'}</Value>
        </FieldGroup>

        <FieldGroup className="span-2">
          <Label>E-mail (Reitor/Diretor)*</Label>
          <Value>{formData.emailReitorDiretor}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Telefone</Label>
          <Value>{formData.telefoneReitorDiretor}</Value>
        </FieldGroup>

        <FieldGroup className="span-2">
          <Label>Número de cooperação técnica (TCT)*</Label>
          <Value>{termoData?.numeroCooperacao || formData.numeroCooperacao || 'Carregando...'}</Value>
        </FieldGroup>

        <FieldGroup>
          <Label>Vigência do convênio*</Label>
          <Value>{termoData?.vigenciaTermo || formData.vigenciaTermo || 'Carregando...'}</Value>
        </FieldGroup>
      </ContentGrid>

      <FileSection>
        <FileLabel>Arquivo: Termo de Cooperação Técnica</FileLabel>
        <FileContainer>
          <FileName>{termoInfo?.nome || 'Carregando...'}</FileName>
          <SubstituirButton onClick={() => navigate('/PreenchaDados')}>
            substituir arquivo
          </SubstituirButton>
        </FileContainer>
      </FileSection>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonContainer>
        {!finalizado ? (
          <>
            <Button onClick={handleBefore} disabled={loading}>
              <span className="material-symbols-outlined" style={{ fontSize: '15px'}}>arrow_back_ios</span>
              Voltar
            </Button>
            <Button onClick={handleNext} disabled={loading || !termoInfo}>
              {loading ? <LoadingSpinner /> : 'Confirmar'}
              {!loading && <span className="material-symbols-outlined" style={{ fontSize: '15px'}}>arrow_forward_ios</span>}
            </Button>
          </>
        ) : (
          <>
            {sucesso && <SuccessMessage>{sucesso}</SuccessMessage>}
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </>
        )}
      </ButtonContainer>
    </FormContainer>
  );
};

export default FormComponent;